import { Box, HStack, VStack } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import AugmentationLayout, {
  AugmentationComponents
} from "@pathwright/ux/src/components/augmentation/AugmentationLayout"
import AugmentationRoutedPanel from "@pathwright/ux/src/components/augmentation/AugmentationRoutedPanel"
import AugmentationToolbar, {
  AugmentationComponent,
  AugmentationToolbarButton,
  AugmentationToolbarOption,
  AugmentationToolbarOptions,
  AugmentationToolbarProps,
  useAugmentationToolbar,
  useOptionLabel
} from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { exhaustiveGuard } from "@pathwright/ux/src/utils/typing"
import { AnimatePresence, motion } from "framer-motion"
import { forwardRef, useEffect, useMemo } from "react"
import { NumberParam, useQueryParam } from "use-query-params"
import { OfferPaymentType, OfferScope } from "@pathwright/graphql-client/src/generated"
import useChangelogAugmentationToolbarOption from "../changelog/augmentation/useChangelogAugmentationToolbarOption"
import useIntercomAugmentationToolbar from "../intercom/augmentation/useIntercomAugmentationToolbar"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import LicenseResourceFlow from "../resource/license/LicenseResourceFlow"
import { PricingButton } from "./CoverPageHeader"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageIsEditable } from "./access"
import { COVER_PAGE_PANEL_ROOT_KEY } from "./constants"
import { CoverPagePanelHome } from "./design/CoverPagePanelHome"
import { useCoverPagePanelChildren } from "./design/CoverPagePanelRoutes"
import {
  useCoverPageTranslate,
  useGetPathUrl,
  useRefetchCoverPageOffers
} from "./utils"

type CoverPageToolbarPosition = "bottom" | "side"

// Rendering the toolbar based on screen size.
// - base: render at bottom of screen
// - md+: render to the right of cover page
export function useCoverPageToolbarPosition<T>(
  breakpointOptions: Record<CoverPageToolbarPosition, T>
): T {
  const isMobile = useCoverPageContext((v) => v.isMobile)
  const position = isMobile ? "bottom" : "side"
  return breakpointOptions[position]
}

// Toolbar positioned on the right side for larger screens
const CoverPageToolbarSide = forwardRef<
  HTMLDivElement,
  AugmentationToolbarProps
>(({ toolbar, yieldsToPanel }, ref) => {
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  return (
    <VStack
      ref={ref}
      position="fixed"
      // Held at the panel's own top rather than the Cover Page's. Anchoring to
      // the card meant opening the panel slid the toolbar 30px up to meet it,
      // and the Cover Page's extent is a concern this toolbar won't share once
      // it's the configuration surface for the rest of the app.
      top={immersiveActive ? "1.5rem" : "10px"}
      right={immersiveActive ? "20px" : "10px"}
      zIndex={12}
      gap={2}
      transition="all 0.3s ease-in-out"
      className="CoverPageAugmentation CoverPageToolbarSide"
      visibility={yieldsToPanel ? "hidden" : "visible"}
      pointerEvents={yieldsToPanel ? "none" : "auto"}
      aria-hidden={yieldsToPanel || undefined}
    >
      <AugmentationToolbar toolbar={toolbar} alwaysVisible />
    </VStack>
  )
})

// Toolbar positioned at the bottom for smaller screens
const CoverPageToolbarBottom = forwardRef<
  HTMLDivElement,
  AugmentationToolbarProps
>(({ toolbar, yieldsToPanel }, ref) => {
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  // For now, we won't render a separate immersive toolbar on mobile
  // since it conflicts with the Immersive navigation bar
  if (immersiveActive) return null

  return (
    <HStack
      ref={ref}
      className="CoverPageAugmentation CoverPageToolbarBottom"
      position="fixed"
      bottom="10px"
      left="10px"
      // Same Intercom corner reservation as Home’s bottom toolbar.
      right="80px"
      gap={2}
      zIndex={101}
      display="flex"
      justifyContent="flex-start"
      visibility={yieldsToPanel ? "hidden" : "visible"}
      pointerEvents={yieldsToPanel ? "none" : "auto"}
      aria-hidden={yieldsToPanel || undefined}
    >
      <AugmentationToolbar toolbar={toolbar} alwaysVisible direction="row" />
    </HStack>
  )
})

const OfferAccessAugmentationComponent: AugmentationComponent<{
  offerId?: string
  paymentType?: string
}> = ({ route }) => {
  const { school } = usePathwrightContext()
  const setOfferId = useCoverPageContext((v) => v.setOfferId)
  // Get the offer conversion intent id from cover page context.
  // This tells us whether the user reached this offer by direct link or from the Offer card.
  // If from the Offer card (via the Offer button), we will attempt to convert the offer
  // automatically, whether that is to register the user or send them to Stripe Checkout.
  const offerConversionIntentId = useCoverPageContext(
    (v) => v.offerConversionIntentId
  )
  const resource = useCoverPageContext((v) => v.resource)
  const offers = useCoverPageContext((v) => v.offers)
  const { offerId: offerIdParam, paymentType } = route?.match.params || {}
  const offerId = Number(offerIdParam)
  const [orderId] = useQueryParam("order_id", NumberParam)
  const inviteToken = useCoverPageContext((v) => v.inviteToken)
  const offer = offers?.find((offer) => offer.id === offerId)

  const refetchCoverPageOffers = useRefetchCoverPageOffers()

  useEffect(() => {
    setOfferId(offerId || null)
    return () => setOfferId(null)
  }, [offerId])

  // Letacy client constants
  const OFFERING_OPTION_IN_PLAN = 1
  const OFFERING_OPTION_NOT_IN_PLAN = 2

  if (offer) {
    switch (offer.scope) {
      case OfferScope.Member:
        const offeringOption = paymentType
          ? paymentType.toUpperCase() === OfferPaymentType.Subscription
            ? OFFERING_OPTION_IN_PLAN
            : OFFERING_OPTION_NOT_IN_PLAN
          : null

        // Get the button label for the payment type, or the first option if no payment type is provided.
        const offerConversionActionLabel =
          offer.price_options.find((priceOption) =>
            paymentType
              ? priceOption.payment_type === paymentType.toUpperCase()
              : true
          )?.button_label ||
          offer.price_options[0]?.button_label ||
          ""

        return (
          // @ts-expect-error: TODO shouldn't use global window object
          <window.OfferRegisterPanelComponent
            resourceSlug={resource?.slug}
            cohortId={offer.cohort_id!}
            orderId={orderId}
            offeringOption={offeringOption}
            inviteToken={inviteToken}
            onOrderFulfilled={refetchCoverPageOffers}
            attemptToConvert={offerConversionIntentId === offer.id}
            offerConversionActionLabel={offerConversionActionLabel}
          />
        )
      case OfferScope.Cohort:
        return (
          // Wrapping with Box to align content to the top (not vertivally centered).
          <Box w="full">
            <LicenseResourceFlow
              id={offer.resource_license_offering_id!}
              licenseeSchool={school}
              orderId={orderId}
              paymentType={paymentType}
              onOrderFulfilled={refetchCoverPageOffers}
            />
          </Box>
        )
      case OfferScope.Path:
        // TODO: implement Path scope (school licensing)
        return <></>
      default:
        exhaustiveGuard(offer.scope)
    }
  }

  return <></>
}

function useOfferAccessToolbarOption(): AugmentationToolbarOption {
  const { t } = useCoverPageTranslate()
  const label = t("toolbar.access", { defaultValue: "Access" })

  return {
    key: "access",
    path: [
      "access/offer/:offerId(\\d+)/",
      "access/offer/:offerId(\\d+)/thank-you/",
      // Optionally, we'll accept a payment type to scope the offer conversion to a specific offer payment type
      // since the user is presented with these options in the Offer card. We must pass this selection on to the
      // conversion flows.
      "access/offer/:offerId(\\d+)/:paymentType(free|one_off|subscription)/",
      "access/offer/:offerId(\\d+)/:paymentType(free|one_off|subscription)/thank-you/"
    ],
    icon: "plus",
    label,
    "aria-label": label,
    isVisible: false,
    AugmentationComponent: OfferAccessAugmentationComponent
  }
}

function ExpandedPricingButton() {
  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: "auto", opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <PricingButton
        collapsed
        rightIcon={<Pathicon icon="chevron-right" size="1.2em" />}
        px={4}
        py={2}
        renderButton={undefined}
      />
    </motion.div>
  )
}

function CollapsedPricingButton({ label }: { label: string }) {
  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: "auto", opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <PricingButton
        collapsed
        leftIcon={undefined}
        px={4}
        py={3}
        renderButton={(props) => {
          const { onClick, to, disabled } = props
          return (
            <AugmentationToolbarButton
              option={{
                key: "pricing-trigger",
                icon: "chevron-right",
                label,
                onClick,
                to,
                disabled
              }}
              isSelected={false}
            />
          )
        }}
      />
    </motion.div>
  )
}

function CoverPagePricingToolbarOption({
  option
}: {
  option: AugmentationToolbarOption
}) {
  const headerCollapsed = useCoverPageContext((v) => v.headerCollapsed)
  const label = useOptionLabel(option)

  return (
    <Box
      position="relative"
      css={{
        "& .chakra-button:not(.chakra-link)": {
          height: "auto",
          maxHeight: "40px"
        }
      }}
    >
      <AnimatePresence mode="wait">
        {headerCollapsed ? (
          <ExpandedPricingButton key="expanded" />
        ) : (
          <CollapsedPricingButton key="collapsed" label={label} />
        )}
      </AnimatePresence>
    </Box>
  )
}

const useToolbarOptions = () => {
  const { t } = useCoverPageTranslate()
  const { licensorSchoolId, immersiveBackTo, isMobile, immersiveActive } =
    useCoverPageContext()
  const coverPageIsEditable = useCoverPageIsEditable()
  const panelChildren = useCoverPagePanelChildren()

  const pathUrl = useGetPathUrl()

  const changelogOption = useChangelogAugmentationToolbarOption()
  const intercomOption = useIntercomAugmentationToolbar()
  const offerAccessOption = useOfferAccessToolbarOption()

  const options: AugmentationToolbarOptions = useMemo(
    () =>
      licensorSchoolId
        ? [
            // TODO: should we include path url when licensee has licensed the offer?
            // [pathUrl ? {
            //   key: "path",
            //   icon: "path",
            //   label: t("toolbar.view_path", { defaultValue: "View Path" }),
            //   to: pathUrl
            // } : null]
          ]
        : [
            [
              immersiveActive
                ? {
                    key: "close",
                    icon: "x-thick",
                    label: t("toolbar.back_to_cover", {
                      defaultValue: "Back to Cover"
                    }),
                    to: immersiveBackTo
                  }
                : null,
              coverPageIsEditable && !immersiveActive
                ? {
                    key: COVER_PAGE_PANEL_ROOT_KEY,
                    icon: "edit" as IconName,
                    label: t("toolbar.edit_cover_page", {
                      defaultValue: "Edit Cover Page"
                    }),
                    "aria-label": t("toolbar.edit_cover_page", {
                      defaultValue: "Edit Cover Page"
                    }),
                    AugmentationLabelComponent: () => (
                      <>{t("panel_header.cover", { defaultValue: "Cover" })}</>
                    ),
                    AugmentationComponent: CoverPagePanelHome,
                    children: panelChildren
                  }
                : null,
              isMobile && !immersiveActive
                ? {
                    key: "pricing",
                    icon: "chevron-right" as IconName,
                    label: t("join", { defaultValue: "Join" }),
                    showInDetailedMenu: false,
                    ToolbarComponent: CoverPagePricingToolbarOption
                  }
                : null,
              offerAccessOption,
              pathUrl && coverPageIsEditable
                ? {
                    key: "path",
                    icon: "path",
                    label: t("toolbar.view_path", {
                      defaultValue: "View Path"
                    }),
                    to: pathUrl
                  }
                : null
            ]
            // [
            // intercomOption
            //   ? {
            //       ...intercomOption,
            //     }
            //   : null,

            // !isMobile && !immersiveActive && changelogOption
            //   ? {
            //       ...changelogOption,
            //     }
            //   : null
            // ]
          ],
    [
      immersiveBackTo,
      isMobile,
      immersiveActive,
      pathUrl,
      coverPageIsEditable,
      panelChildren,
      changelogOption,
      intercomOption,
      licensorSchoolId,
      offerAccessOption,
      t
    ]
  )

  return options
}

const CoverPageAugmentation = ({
  Primary
}: {
  Primary: AugmentationComponents["Primary"]
}) => {
  // Build toolbar options using extracted components
  const options: AugmentationToolbarOptions = useToolbarOptions()
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const designPanelDisclosure = useCoverPageContext(
    (v) => v.designPanelDisclosure
  )

  const CoverPageToolbarComponent = useCoverPageToolbarPosition({
    bottom: CoverPageToolbarBottom,
    side: CoverPageToolbarSide
  })

  const toolbar = useAugmentationToolbar(options)

  return (
    <Box
      w="100%"
      h="100%"
      pointerEvents="none"
      css={{
        "& .AugmentationLayout, & .AugmentationLayoutContent": {
          pointerEvents: "none"
        },
        "& .AugmentationLayoutPrimary, & .CoverPageAugmentation, & .AugmentationPanel":
          {
            pointerEvents: "auto"
          },
        "& .chakra-drawer__body, & .chakra-drawer__body > div": {
          height: "100%"
        }
      }}
    >
      <AugmentationLayout
        key={`cover-page-augmentation-toolbar-${resourceId}`}
        components={{
          Primary,
          Panel: AugmentationRoutedPanel,
          Toolbar: CoverPageToolbarComponent
        }}
        toolbar={toolbar}
        disclosure={designPanelDisclosure}
        spacing={[30, 10, 10]}
        allowOverlay
        toolbarYieldsToPanel
        panelPosition="far"
        align="right"
        offset={{ mode: "constrain", safeInset: 60, primaryMaxWidth: 1000 }}
        panelSize={{ base: "full", md: "xs" }}
      />
    </Box>
  )
}

export default CoverPageAugmentation
